// Hook pour précharger les données d'une page
import { useState, useCallback, useRef } from 'react';
import type { LayoutItem } from '../Config/config';
import { LazyLoadLogger } from '../Services/LazyLoadLogger';

export interface PageCacheEntry {
    url: string;
    items: LayoutItem[];
    timestamp: number;
    metadata?: Record<string, any>;
}

export interface UsePagePreloadReturn {
    preloadPage: (url: string, loader: () => Promise<LayoutItem[]>) => Promise<void>;
    getCachedPage: (url: string) => PageCacheEntry | null;
    clearCache: (url?: string) => void;
    isPreloading: (url: string) => boolean;
    getCache: () => Map<string, PageCacheEntry>;
}

/**
 * Hook pour précharger les données des pages
 * Stocke en cache les instances de modules et métadonnées
 */
export function usePagePreload(): UsePagePreloadReturn {
    const [cache, setCache] = useState<Map<string, PageCacheEntry>>(new Map());
    const [preloading, setPreloading] = useState<Set<string>>(new Set());
    const cacheRef = useRef<Map<string, PageCacheEntry>>(new Map());
    const preloadingRef = useRef<Set<string>>(new Set());

    // Sync refs
    cacheRef.current = cache;
    preloadingRef.current = preloading;

    /**
     * Précharge une page
     */
    const preloadPage = useCallback(async (
        url: string,
        loader: () => Promise<LayoutItem[]>
    ): Promise<void> => {
        // Si déjà en cache, ne rien faire
        if (cacheRef.current.has(url)) {
            LazyLoadLogger.logPreload(`Page déjà en cache: ${url}`);
            return;
        }

        // Si déjà en cours de préchargement, attendre
        if (preloadingRef.current.has(url)) {
            LazyLoadLogger.logPreload(`Préchargement déjà en cours pour: ${url}`);
            return;
        }

        // Marquer comme en cours
        setPreloading(prev => new Set(prev).add(url));

        LazyLoadLogger.time(`Préchargement de ${url}`);
        LazyLoadLogger.logPreload(`Début du préchargement: ${url}`);

        try {
            const items = await loader();

            const entry: PageCacheEntry = {
                url,
                items,
                timestamp: Date.now(),
                metadata: {
                    itemsCount: items.length
                }
            };

            setCache(prev => {
                const newCache = new Map(prev);
                newCache.set(url, entry);
                return newCache;
            });

            LazyLoadLogger.timeEnd(`Préchargement de ${url}`);
            LazyLoadLogger.logPreload(`✅ Page préchargée: ${url}`, {
                itemsCount: items.length,
                cacheSize: cacheRef.current.size + 1
            });
        } catch (error) {
            LazyLoadLogger.error('preload', `❌ Erreur de préchargement pour: ${url}`, error);
        } finally {
            // Retirer de la liste des préchargements en cours
            setPreloading(prev => {
                const newSet = new Set(prev);
                newSet.delete(url);
                return newSet;
            });
        }
    }, []);

    /**
     * Récupère une page du cache
     */
    const getCachedPage = useCallback((url: string): PageCacheEntry | null => {
        return cache.get(url) || null;
    }, [cache]);

    /**
     * Vide le cache
     */
    const clearCache = useCallback((url?: string) => {
        if (url) {
            setCache(prev => {
                const newCache = new Map(prev);
                newCache.delete(url);
                return newCache;
            });
            LazyLoadLogger.logPreload(`Cache vidé pour: ${url}`);
        } else {
            setCache(new Map());
            LazyLoadLogger.logPreload('Cache complètement vidé');
        }
    }, []);

    /**
     * Vérifie si une page est en cours de préchargement
     */
    const isPreloading = useCallback((url: string): boolean => {
        return preloading.has(url);
    }, [preloading]);

    /**
     * Récupère tout le cache
     */
    const getCache = useCallback(() => {
        return new Map(cache);
    }, [cache]);

    return {
        preloadPage,
        getCachedPage,
        clearCache,
        isPreloading,
        getCache
    };
}



